import jsLogger from 'js-logger'

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

// Above this RMS-ish peak a track is considered "carrying voice" rather than
// noise floor. Real avatar/mic speech peaks at 0.1–0.7; dead tracks sit at 0
// and noise floor stays under ~0.005.
const ACTIVE_THRESHOLD = 0.02
// How long an elected winner may stay silent before we allow failover to
// another active track. Comfortably longer than natural speech pauses.
const FAILOVER_SILENCE_MS = 4000

type TrackChannel = {
  label: string
  gain: GainNode
  level: () => number
}

class AudioMixer {
  private ctx: AudioContext
  private destination: MediaStreamAudioDestinationNode
  private clonedTracks: MediaStreamTrack[] = []
  private processors: ScriptProcessorNode[] = []
  private sources: AudioNode[] = []
  private sinks: HTMLAudioElement[] = []
  private monitors: number[] = []

  constructor() {
    this.ctx = new AudioContext({
      latencyHint: 'interactive'
    })
    this.destination = this.ctx.createMediaStreamDestination()
    this.destination.channelCount = 1
    this.destination.channelCountMode = 'explicit'
  }

  async addStream(stream: MediaStream, label = 'unlabeled') {
    const inputTracks = stream.getAudioTracks()
    jsLogger.log('AudioMixer.addStream', {
      label,
      audioTrackCount: inputTracks.length,
      ctxState: this.ctx.state
    })

    if (inputTracks.length === 0) {
      await this.resume()
      return
    }
    await this.resume()

    if (inputTracks.length === 1) {
      // Single track (e.g. mic) — connect at full gain, nothing to elect.
      this.connectTrack(inputTracks[0], label, 1)
      return
    }

    // HeyGen ships the avatar with two remote audio tracks. Their behaviour
    // is inconsistent across platforms:
    //  - Safari/Mac: only one track ever carries samples, the other is dead,
    //    and which one is alive is randomised per session.
    //  - Chrome/Windows: BOTH tracks carry the voice, slightly out of sync,
    //    so summing them produces a doubled "echo" voice.
    // So we can neither pick blindly (lottery → silence) nor sum them all
    // (echo). Connect every track behind its own gain gate starting muted,
    // then elect a single active track to un-gate. Re-elect (failover) only
    // if the winner goes quiet while another track is active.
    const channels = inputTracks.map((track, idx) =>
      this.connectTrack(track, `${label}#${idx}`, 0)
    )
    this.electWinner(channels, label)
  }

  private connectTrack(
    originalTrack: MediaStreamTrack,
    label: string,
    initialGain: number
  ): TrackChannel {
    // Clone the track so the original (consumed by the playback <video>) is
    // untouched. The sink keeps WebKit's media pipeline actively pulling
    // data through the clone; without a media-element consumer remote WebRTC
    // tracks deliver nothing to Web Audio.
    const cloned = originalTrack.clone()
    this.clonedTracks.push(cloned)

    jsLogger.log('AudioMixer.connectTrack', {
      label,
      initialGain,
      original: {
        id: originalTrack.id,
        label: originalTrack.label,
        readyState: originalTrack.readyState,
        muted: originalTrack.muted,
        enabled: originalTrack.enabled
      },
      cloned: { id: cloned.id }
    })

    cloned.addEventListener('ended', () =>
      jsLogger.warn('AudioMixer: track ended', { label, id: cloned.id })
    )
    originalTrack.addEventListener('ended', () =>
      jsLogger.warn('AudioMixer: original ended', { label, id: originalTrack.id })
    )

    const trackStream = new MediaStream([cloned])

    const sink = document.createElement('audio')
    // muted, not volume = 0: iOS Safari's HTMLMediaElement.volume is read-only
    // (hardware-controlled), so volume = 0 is a no-op there and the sink would
    // play the mic track back through the speaker, causing audible echo.
    sink.muted = true
    sink.volume = 0
    sink.autoplay = true
    // playsInline only exists on HTMLVideoElement in the DOM types and is a
    // no-op for <audio>; set it as an attribute to keep WebKit happy without
    // tripping TS.
    sink.setAttribute('playsinline', 'true')
    sink.setAttribute('aria-hidden', 'true')
    sink.style.display = 'none'
    sink.srcObject = trackStream
    document.body.appendChild(sink)
    const playPromise = sink.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((e) =>
        jsLogger.warn('AudioMixer: sink play() rejected', {
          label,
          error: e && e.message
        })
      )
    }
    this.sinks.push(sink)

    const source = this.ctx.createMediaStreamSource(trackStream)
    this.sources.push(source)

    const gain = this.ctx.createGain()
    gain.gain.value = initialGain

    // Parallel tap for level measurement — analyser has no downstream
    // connection, it only reads what the active source feeds it.
    const analyser = this.ctx.createAnalyser()
    analyser.fftSize = 2048
    const buf = new Float32Array(analyser.fftSize)
    const level = () => {
      analyser.getFloatTimeDomainData(buf)
      let max = 0
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs(buf[i])
        if (v > max) max = v
      }
      return max
    }
    source.connect(analyser)

    if (isSafari) {
      // ScriptProcessor forces Safari to actively pull audio data through
      // the graph — without it, WebKit doesn't read from the source even
      // with the sink attached.
      const processor = this.ctx.createScriptProcessor(4096, 1, 1)
      processor.onaudioprocess = (e) => {
        e.outputBuffer.getChannelData(0).set(e.inputBuffer.getChannelData(0))
      }
      source.connect(processor)
      processor.connect(gain)
      this.processors.push(processor)
    } else {
      source.connect(gain)
    }
    gain.connect(this.destination)

    return { label, gain, level }
  }

  // Watch a set of channels, un-gate the first one that carries voice, keep
  // the rest muted. Allow failover if the winner goes silent while another
  // channel is active.
  private electWinner(channels: TrackChannel[], label: string) {
    let winner = -1
    let winnerSilentSince = 0

    const setWinner = (idx: number) => {
      channels.forEach((c, i) => {
        c.gain.gain.value = i === idx ? 1 : 0
      })
      winner = idx
      winnerSilentSince = 0
    }

    const monitor = window.setInterval(() => {
      const levels = channels.map((c) => c.level())

      if (winner === -1) {
        const idx = levels.findIndex((l) => l > ACTIVE_THRESHOLD)
        if (idx !== -1) {
          setWinner(idx)
          jsLogger.log('AudioMixer: avatar track elected', {
            label,
            winner: channels[idx].label,
            levels
          })
        }
        return
      }

      if (levels[winner] > ACTIVE_THRESHOLD) {
        winnerSilentSince = 0
        return
      }
      // Winner is quiet — only failover if it stays quiet AND another track
      // is currently active (avoids switching during natural speech pauses).
      if (winnerSilentSince === 0) {
        winnerSilentSince = Date.now()
      } else if (Date.now() - winnerSilentSince > FAILOVER_SILENCE_MS) {
        const alt = levels.findIndex(
          (l, i) => i !== winner && l > ACTIVE_THRESHOLD
        )
        if (alt !== -1) {
          jsLogger.warn('AudioMixer: avatar track failover', {
            label,
            from: channels[winner].label,
            to: channels[alt].label,
            levels
          })
          setWinner(alt)
        }
      }
    }, 150)

    this.monitors.push(monitor)
  }

  getStream(): MediaStream {
    return this.destination.stream
  }

  async resume() {
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume()
    }
  }

  close() {
    this.monitors.forEach((m) => window.clearInterval(m))
    this.monitors = []
    this.sources.forEach((s) => s.disconnect())
    this.sources = []
    this.processors.forEach((p) => p.disconnect())
    this.processors = []
    this.sinks.forEach((s) => {
      try {
        s.pause()
        s.srcObject = null
        s.remove()
      } catch {}
    })
    this.sinks = []
    this.clonedTracks.forEach((t) => t.stop())
    this.clonedTracks = []
    this.ctx.close()
  }
}

export default AudioMixer
